import React, { useState, useEffect, useMemo } from 'react';
import {
  Camera, CheckCircle, XCircle, Loader, PauseCircle, HelpCircle,
  RefreshCw, Search, WifiOff, History,
} from 'lucide-react';
import api from '../services/api';

// Estado de las capturas, canal por canal. Lo escribe el scheduler en
// Snapshot_Estado (migracion 0011).
//
// La distincion que importa en esta pagina: un canal en ERROR cuyo equipo esta
// OFFLINE es lo esperado (el vehiculo esta apagado) y va en gris; uno en error con
// el equipo EN LINEA es el fallo de verdad y va en rojo. Esa segunda categoria es
// la que estuvo 64 dias invisible en un equipo.
const ESTADOS = {
  completo:   { label: 'Completo',   color: '#065f46', bg: '#d1fae5', Icon: CheckCircle },
  solicitado: { label: 'Solicitado', color: '#1e40af', bg: '#dbeafe', Icon: Loader },
  error:      { label: 'Error',      color: '#991b1b', bg: '#fee2e2', Icon: XCircle },
  pospuesto:  { label: 'Pospuesto',  color: '#92400e', bg: '#fef3c7', Icon: PauseCircle },
  sin_datos:  { label: 'Sin datos',  color: '#374151', bg: '#f3f4f6', Icon: HelpCircle },
};
const OFFLINE = { label: 'Equipo offline', color: '#4b5563', bg: '#f3f4f6', Icon: WifiOff };
// Canal que ya no esta en la lista del equipo: su estado viejo se sigue viendo,
// pero no es algo que atender -- a ese canal ya nadie le pide imagen.
const HUERFANO = { label: 'Fuera de catalogo', color: '#4b5563', bg: '#e5e7eb', Icon: HelpCircle };

const hace = (iso) => {
  if (!iso) return 'nunca';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `hace ${Math.round(s)} s`;
  if (s < 3600) return `hace ${Math.round(s / 60)} min`;
  if (s < 86400) return `hace ${Math.round(s / 3600)} h`;
  return `hace ${Math.round(s / 86400)} d`;
};

const Capturas = () => {
  const [canales, setCanales] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [errores, setErrores] = useState([]);
  const [filtro, setFiltro] = useState('problemas');   // problemas|todos|<estado>
  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(true);
  const [verHistorial, setVerHistorial] = useState(false);

  const load = async () => {
    try {
      const [s, e] = await Promise.all([
        api.get('/api/captures/status'),
        api.get('/api/captures/errors?limit=200'),
      ]);
      setCanales(s.data?.canales || []);
      setResumen(s.data?.resumen || null);
      setErrores(e.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const filtradas = useMemo(() => {
    let data = canales;
    if (filtro === 'problemas') {
      // Lo que de verdad hay que atender: falla, el equipo esta conectado y el
      // canal sigue en su catalogo (un huerfano no se puede arreglar: ya no se
      // le pide imagen, solo ensuciaba la lista para siempre).
      data = data.filter(c => c.estado === 'error' && c.is_online && !c.huerfano);
    } else if (filtro !== 'todos') {
      data = data.filter(c => c.estado === filtro);
    }
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      data = data.filter(c =>
        (c.device_id || '').toLowerCase().includes(q) ||
        (c.device_name || '').toLowerCase().includes(q) ||
        (c.numero_unidad || '').toString().toLowerCase().includes(q) ||
        (c.motivo || '').toLowerCase().includes(q));
    }
    // Primero lo roto en equipos vivos, luego por racha de errores.
    return [...data].sort((a, b) => {
      const urge = (c) => (c.estado === 'error' && c.is_online && !c.huerfano) ? 0 : 1;
      const pa = urge(a);
      const pb = urge(b);
      if (pa !== pb) return pa - pb;
      if ((b.errores_seguidos || 0) !== (a.errores_seguidos || 0)) {
        return (b.errores_seguidos || 0) - (a.errores_seguidos || 0);
      }
      return String(a.numero_unidad || a.device_id)
        .localeCompare(String(b.numero_unidad || b.device_id), 'es', { numeric: true });
    });
  }, [canales, filtro, busqueda]);

  const nombre = (c) => c.numero_unidad ? `MDVR ${c.numero_unidad}` : (c.device_name || c.device_id);

  // La imagen NO puede ser un <a href>: /api/snapshots/{id} exige Bearer y el
  // navegador no manda cabeceras en una navegacion normal (daria 401). Se baja con
  // axios, que si lleva el token, y se abre el blob.
  const verImagen = async (snapshotId) => {
    try {
      const r = await api.get(`/api/snapshots/${snapshotId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.error(e);
      alert('No se pudo abrir la imagen');
    }
  };

  const Badge = ({ fila }) => {
    // Un error con el equipo caido no es el mismo problema que uno con el equipo
    // en linea: se pintan distinto a proposito.
    const d = fila.huerfano ? HUERFANO
      : (fila.estado === 'error' && !fila.is_online) ? OFFLINE
      : (ESTADOS[fila.estado] || ESTADOS.sin_datos);
    const { Icon } = d;
    return (
      <span title={fila.huerfano
        ? 'Este canal ya no esta en la lista del equipo: no se captura. Es estado viejo.'
        : undefined}
        style={{ ...S.badge, background: d.bg, color: d.color }}>
        <Icon size={13} className={fila.estado === 'solicitado' ? 'spin' : ''} /> {d.label}
      </span>
    );
  };

  return (
    <div style={S.container}>
      <header style={S.header}>
        <div>
          <h1 style={S.title}><Camera size={26} /> Capturas</h1>
          <p style={S.subtitle}>
            Estado de la última captura de cada canal. Se refresca cada 30 s.
          </p>
        </div>
        <button style={S.refreshBtn} onClick={load}>
          <RefreshCw size={16} className={loading ? 'spin' : ''} /> Actualizar
        </button>
      </header>

      {resumen && (
        <div style={S.kpis}>
          <div style={{ ...S.kpi, borderLeftColor: '#dc2626' }}>
            <div style={S.kpiVal}>{resumen.error_en_linea}</div>
            <div style={S.kpiLab}>Fallando <b>en línea</b></div>
          </div>
          <div style={{ ...S.kpi, borderLeftColor: '#10b981' }}>
            <div style={S.kpiVal}>{resumen.completo}</div>
            <div style={S.kpiLab}>Con imagen</div>
          </div>
          <div style={{ ...S.kpi, borderLeftColor: '#3b82f6' }}>
            <div style={S.kpiVal}>{resumen.solicitado}</div>
            <div style={S.kpiLab}>En curso</div>
          </div>
          <div style={{ ...S.kpi, borderLeftColor: '#f59e0b' }}>
            <div style={S.kpiVal}>{resumen.pospuesto}</div>
            <div style={S.kpiLab}>Pospuestos</div>
          </div>
          <div style={{ ...S.kpi, borderLeftColor: '#6b7280' }}>
            <div style={S.kpiVal}>{resumen.error - resumen.error_en_linea}</div>
            <div style={S.kpiLab}>Sin capturar, equipo apagado</div>
          </div>
          <div style={{ ...S.kpi, borderLeftColor: '#9ca3af' }}>
            <div style={S.kpiVal}>{resumen.fuera_catalogo ?? 0}</div>
            <div style={S.kpiLab}>Fuera de catálogo</div>
          </div>
          <div style={{ ...S.kpi, borderLeftColor: '#374151' }}>
            <div style={S.kpiVal}>{resumen.total}</div>
            <div style={S.kpiLab}>Canales totales</div>
          </div>
        </div>
      )}

      <div style={S.controls}>
        <div style={S.tabs}>
          {[
            ['problemas', 'Con problema'],
            ['todos', 'Todos'],
            ['completo', 'Completos'],
            ['error', 'Errores'],
            ['pospuesto', 'Pospuestos'],
            ['sin_datos', 'Sin datos'],
          ].map(([k, label]) => (
            <button key={k} onClick={() => setFiltro(k)}
                    style={{ ...S.tab, ...(filtro === k ? S.tabActive : {}) }}>
              {label}
            </button>
          ))}
        </div>
        <div style={S.searchBox}>
          <Search size={14} color="#9ca3af" />
          <input style={S.search} placeholder="Unidad, serial o motivo…"
                 value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        </div>
      </div>

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Equipo</th>
              <th style={S.th}>Canal</th>
              <th style={S.th}>Estado</th>
              <th style={S.th}>Cuándo</th>
              <th style={S.th}>Pidió</th>
              <th style={S.th}>Motivo</th>
              <th style={S.thNum}>Fallos seguidos</th>
              <th style={S.th}>Última imagen</th>
            </tr>
          </thead>
          <tbody>
            {filtradas.length === 0 && (
              <tr><td colSpan={8} style={S.empty}>
                {filtro === 'problemas'
                  ? 'Ningún canal está fallando con el equipo en línea.'
                  : 'Sin canales en este filtro.'}
              </td></tr>
            )}
            {filtradas.map(c => (
              <tr key={`${c.device_id}-${c.channel}`} style={S.tr}>
                <td style={S.td}>
                  <b>{nombre(c)}</b>
                  <div style={S.mono}>{c.device_id}</div>
                  {c.huerfano && <span style={S.tagGris}>canal fuera del catálogo</span>}
                </td>
                <td style={S.td}>{c.channel}</td>
                <td style={S.td}><Badge fila={c} /></td>
                <td style={S.td}>{hace(c.terminado_at || c.solicitado_at)}</td>
                <td style={S.td}>{c.origen || '—'}</td>
                <td style={{ ...S.td, maxWidth: 320 }}>{c.motivo || '—'}</td>
                <td style={S.tdNum}>
                  {c.errores_seguidos > 0
                    ? <span style={S.racha}>{c.errores_seguidos}</span>
                    : '—'}
                </td>
                <td style={S.td}>
                  {c.snapshot_id
                    ? <button style={S.link} onClick={() => verImagen(c.snapshot_id)}>ver</button>
                    : '—'}
                  <div style={S.sub}>{hace(c.ultimo_ok_at)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button style={S.histBtn} onClick={() => setVerHistorial(v => !v)}>
        <History size={15} /> {verHistorial ? 'Ocultar' : 'Ver'} historial de errores ({errores.length})
      </button>

      {verHistorial && (
        <div style={S.card}>
          <p style={S.nota}>
            El historial está deduplicado: un canal que falla cada minuto por lo
            mismo deja una fila cada 30 min, no una por intento. La cuenta real va
            en “fallos seguidos”.
          </p>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Cuándo</th>
                <th style={S.th}>Equipo</th>
                <th style={S.th}>Canal</th>
                <th style={S.th}>Pidió</th>
                <th style={S.th}>Motivo</th>
                <th style={S.thNum}>Racha</th>
              </tr>
            </thead>
            <tbody>
              {errores.length === 0 && (
                <tr><td colSpan={6} style={S.empty}>Sin errores registrados.</td></tr>
              )}
              {errores.map(e => (
                <tr key={e.id} style={S.tr}>
                  <td style={S.td}>{new Date(e.ocurrio_at).toLocaleString('es-MX')}</td>
                  <td style={S.td}>
                    {e.numero_unidad ? `MDVR ${e.numero_unidad}` : (e.device_name || e.device_id)}
                    <div style={S.mono}>{e.device_id}</div>
                  </td>
                  <td style={S.td}>{e.channel}</td>
                  <td style={S.td}>{e.origen || '—'}</td>
                  <td style={S.td}>{e.motivo || '—'}</td>
                  <td style={S.tdNum}>{e.racha ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

const S = {
  container: { padding: 24, maxWidth: 1400, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title: { fontSize: 24, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 },
  subtitle: { fontSize: 13, color: '#6b7280', marginTop: 4 },
  refreshBtn: { display: 'flex', gap: 6, alignItems: 'center', padding: '8px 14px',
                background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer' },

  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 },
  kpi: { background: 'white', padding: 14, borderRadius: 8, borderLeft: '4px solid',
         boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  kpiVal: { fontSize: 22, fontWeight: 700 },
  kpiLab: { fontSize: 12, color: '#6b7280', marginTop: 4 },

  controls: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              gap: 12, marginBottom: 14, flexWrap: 'wrap' },
  tabs: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  tab: { padding: '7px 14px', borderRadius: 8, border: '1px solid #e5e7eb',
         background: 'white', cursor: 'pointer', fontSize: 13 },
  tabActive: { background: '#3b82f6', color: 'white', borderColor: '#3b82f6' },
  searchBox: { display: 'flex', alignItems: 'center', gap: 6, background: 'white',
               border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 10px' },
  search: { border: 'none', outline: 'none', fontSize: 13, width: 220 },

  card: { background: 'white', borderRadius: 8, overflow: 'auto',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)', marginBottom: 14 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb',
        color: '#6b7280', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' },
  thNum: { textAlign: 'right', padding: '10px 12px', borderBottom: '1px solid #e5e7eb',
           color: '#6b7280', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '10px 12px', verticalAlign: 'top' },
  tdNum: { padding: '10px 12px', textAlign: 'right', verticalAlign: 'top' },
  mono: { fontFamily: 'monospace', fontSize: 11, color: '#9ca3af' },
  sub: { fontSize: 11, color: '#9ca3af' },
  badge: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px',
           borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' },
  racha: { display: 'inline-block', minWidth: 26, padding: '2px 7px', borderRadius: 999,
           background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: 12 },
  tagGris: { display: 'inline-block', marginTop: 4, fontSize: 11, background: '#f3f4f6',
             color: '#6b7280', padding: '1px 6px', borderRadius: 4 },
  link: { color: '#2563eb', background: 'none', border: 'none', padding: 0,
          cursor: 'pointer', font: 'inherit', textDecoration: 'underline' },
  empty: { padding: 40, textAlign: 'center', color: '#6b7280' },
  nota: { margin: 0, padding: '10px 12px', fontSize: 12, color: '#6b7280',
          background: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
  histBtn: { display: 'flex', alignItems: 'center', gap: 7, padding: '8px 14px',
             background: 'white', border: '1px solid #e5e7eb', borderRadius: 8,
             cursor: 'pointer', fontSize: 13, marginBottom: 14 },
};

export default Capturas;
